import Typography from '@mui/material/Typography';
import { ViewInvoiceDetail as ViewInvoiceDetailAtom } from 'atoms';
import { ContainerDiv } from 'common/ContainerDiv';
import CreateText from 'common/CreateText';
import TableStructure from 'common/TableStructure';
import StatusBadge from 'common/StatusBadge';
import Pagination from 'common/Pagination';
import { FormattedMessage } from 'react-intl';
import { useRecoilState } from 'recoil';

import React, { useState, useMemo } from 'react';

import CreateInvoice from './Partials/CreateInvoice';
import AppointmentPaymentPopup from '../AppointmentPaymentPopup';
import PurchasedServicePaymentPopup from '../PurchasedServicePaymentPopup';

export default function InvoiceTable({
    rows,
    handleDelete,
    loading,
    noDataMessage,
    getInvoices,
    totalItems,
    currentPage,
    totalPages,
    onPageChange,
    itemsPerPage,
    sortOption = 'date_newest',
    providers = [],
    servicesList = [],
    providerFullDetails = [],
}) {
    const [invoiceToView, setInvoiceToView] = useRecoilState(ViewInvoiceDetailAtom);
    const [isInvoicePopupOpen, setIsInvoicePopupOpen] = useState(false);
    const [isAppointmentPopupOpen, setIsAppointmentPopupOpen] = useState(false);
    const [isPurchasedServicePopupOpen, setIsPurchasedServicePopupOpen] = useState(false);
    const [appointmentData, setAppointmentData] = useState(null);
    const [purchasedServiceData, setPurchasedServiceData] = useState(null);
    const columns = useMemo(() => {
        const baseColumns = [
            {
                field: 'id',
                headerName: <FormattedMessage id="be.seen.invoice.table.id" defaultMessage="ID" />, 
                flex: 0.4,
                minWidth: 80,
            },
            {
                field: 'date',
                headerName: <FormattedMessage id="be.seen.invoice.table.date" defaultMessage="DATE" />, 
                flex: 1,
                minWidth: 120,
            },
            {
                field: 'status',
                headerName: <FormattedMessage id="be.seen.invoice.table.status" defaultMessage="STATUS" />, 
                flex: 0.8,
                minWidth: 120,
                renderCell: (params) => <StatusBadge status={params?.row?.status} />, 
            },
            {
                field: 'payment_type',
                headerName: <FormattedMessage id="be.seen.invoice.table.paymentType" defaultMessage="PAYMENT TYPE" />, 
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'provider',
                headerName: <FormattedMessage id="be.seen.invoice.table.provider" defaultMessage="PROVIDER/BUSINESS" />, 
                flex: 1.2,
                minWidth: 150,
            },
            {
                field: 'customer',
                headerName: <FormattedMessage id="be.seen.invoice.table.customer" defaultMessage="CUSTOMER" />, 
                flex: 1.5,
                minWidth: 200,
                renderCell: (params) => (
                    <Typography component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <span style={{ fontSize: '14px', color: '#172B36', fontWeight: 500 }}>
                            {params.row.customer || '-'}
                        </span>
                        {params.row.customer_info && (
                            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                                {params.row.customer_info}
                            </span>
                        )}
                    </Typography>
                ),
            },
            {
                field: 'total',
                headerName: <FormattedMessage id="be.seen.invoice.table.total" defaultMessage="AMOUNT" />, 
                flex: 0.8,
                minWidth: 100,
                align: 'right',
                headerAlign: 'right',
            },
            {
                field: 'chevron',
                headerName: '',
                sortable: false,
                flex: 0.2,
                minWidth: 60,
                renderCell: () => (
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={{ marginLeft: 'auto' }}
                    >
                        <path d="M9 18L15 12L9 6" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ),
            },
        ];

        if (sortOption === 'customer_az' || sortOption === 'customer_za') {
            const reordered = [...baseColumns];
            const customerColumn = reordered.splice(5, 1)[0];
            reordered.splice(1, 0, customerColumn);
            return reordered;
        }

        if (sortOption === 'amount_high' || sortOption === 'amount_low') {
            const reordered = [...baseColumns];
            const totalColumn = reordered.splice(6, 1)[0];
            reordered.splice(1, 0, {
                ...totalColumn,
                align: 'left',
                headerAlign: 'left',
            });
            return reordered;
        }

        return baseColumns;
    }, [sortOption]);

    const handleRowClick = (params) => {
        if (!params || !params.row || !params.row._originalData) {
            return;
        }

        const paymentType = params.row._paymentType;
        const originalData = params.row._originalData;

        if (paymentType === 'invoice') {
            setInvoiceToView(originalData);
            setIsInvoicePopupOpen(true);
            setIsAppointmentPopupOpen(false);
            setIsPurchasedServicePopupOpen(false);
        } else if (paymentType === 'appointment') {
            setAppointmentData(originalData);
            setIsAppointmentPopupOpen(true);
            setIsInvoicePopupOpen(false);
            setIsPurchasedServicePopupOpen(false);
        } else if (paymentType === 'purchased_service') {
            setPurchasedServiceData(originalData);
            setIsPurchasedServicePopupOpen(true);
            setIsInvoicePopupOpen(false);
            setIsAppointmentPopupOpen(false);
        }
    };

    const tableRows = useMemo(() => rows ?? [], [rows]);

    return (
        <div>
            {loading ? (
                <ContainerDiv>
                    <span className="text-xl font-bold animate-pulse text-gray-500">...Fetching Payments</span>
                </ContainerDiv>
            ) : tableRows.length > 0 ? (
                <>
                    <TableStructure
                        rows={tableRows}
                        columns={columns}
                        handleRowClick={handleRowClick}
                    />
                </>
            ) : (
                <div style={{ position: 'relative' }}>
                    <div className="empty">
                        <TableStructure
                            rows={[]}
                            columns={columns}
                            handleRowClick={() => {}}
                        />
                    </div>
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(40px + 35%)',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            color: '#666',
                            fontSize: '16px',
                            lineHeight: '1.5',
                            fontFamily: 'Poppins, sans-serif',
                            pointerEvents: 'none',
                        }}
                    >
                        {noDataMessage || 'No appointments on this day'}
                    </div>
                </div>
            )}
            <CreateInvoice
                isPopupOpen={isInvoicePopupOpen}
                getInvoices={getInvoices}
                setIsOpenPopup={setIsInvoicePopupOpen}
                providers={providers}
                servicesList={servicesList}
                providerFullDetails={providerFullDetails}
            />
            <AppointmentPaymentPopup
                open={isAppointmentPopupOpen}
                onClose={() => {
                    setIsAppointmentPopupOpen(false);
                    setTimeout(() => setAppointmentData(null), 300);
                }}
                appointmentData={appointmentData}
            />
            <PurchasedServicePaymentPopup
                open={isPurchasedServicePopupOpen}
                onClose={() => {
                    setIsPurchasedServicePopupOpen(false);
                    setTimeout(() => setPurchasedServiceData(null), 300);
                }}
                purchasedServiceData={purchasedServiceData}
            />
            {!loading && tableRows.length > 0 && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    itemsPerPage={itemsPerPage}
                    totalItems={totalItems}
                />
            )}
        </div>
    );
}
